import alertSound from "../NotesErrorState/soundNoteInterrupt/alert.mp3";

let audioUnlocked = false;

// Dedicated element for the initial "unlock" gesture
const unlockAudioEl = new Audio(alertSound);
unlockAudioEl.volume = 1;
unlockAudioEl.preload = "auto";

// Single shared audio instance for actual playback
// This respects browser autoplay policies since it's unlocked via user gesture
const sharedAudio = new Audio(alertSound);
sharedAudio.volume = 1;
sharedAudio.preload = "auto";
// Preload the audio immediately so it's ready when needed
sharedAudio.load();

// Track if audio is currently playing to avoid interrupting
let isPlaying = false;

/**
 * Unlock audio once using a real user gesture
 */
export function unlockGlobalAudio() {
    if (audioUnlocked) return Promise.resolve();

    unlockAudioEl.muted = true;

    return unlockAudioEl
        .play()
        .then(() => {
            unlockAudioEl.pause();
            unlockAudioEl.currentTime = 0;
            unlockAudioEl.muted = false;
            audioUnlocked = true;
        })
        .catch(() => { });
}

/**
 * Play audio only if already unlocked.
 * Properly resets and reloads the audio if it's stuck loading,
 * ensuring it plays every time without creating new instances.
 */
export function playGlobalAudio() {
    if (!audioUnlocked) return;

    // If already playing, stop it
    if (isPlaying) {
        sharedAudio.pause();
        sharedAudio.currentTime = 0;
    }

    // Only reload if the audio is in a bad state (error or network error)
    // Since we preload on initialization, we don't need to reload every time
    if (sharedAudio.readyState === 0 || sharedAudio.error) {
        sharedAudio.load();
    }

    // Reset to beginning
    sharedAudio.currentTime = 0;

    // Set up event listeners to track playing state
    const handleEnded = () => {
        isPlaying = false;
        sharedAudio.removeEventListener('ended', handleEnded);
    };

    const handleError = () => {
        isPlaying = false;
        sharedAudio.removeEventListener('error', handleError);
    };

    sharedAudio.addEventListener('ended', handleEnded, { once: true });
    sharedAudio.addEventListener('error', handleError, { once: true });

    // Play the audio - browser will buffer and play when ready
    // load() ensures fresh state, and play() will work once enough data is loaded
    isPlaying = true;
    sharedAudio.play().catch(() => {
        isPlaying = false;
        // If play fails immediately, wait for canplay and retry
        const retryPlay = () => {
            sharedAudio.removeEventListener('canplay', retryPlay);
            isPlaying = true;
            sharedAudio.play().catch(() => {
                isPlaying = false;
            });
        };
        sharedAudio.addEventListener('canplay', retryPlay, { once: true });
    });
}

/**
 * Optional helpers
 */
export function isAudioUnlocked() {
    return audioUnlocked;
}